import React from 'react';
import { FaTimes } from 'react-icons/fa';
import { EvidenceQuote } from './AssessmentCriterion';
import { MAX_QUOTES, quoteCount } from '../common/evidencePayload';
import { alreadyOnQuestion } from '../common/evidenceDraft';

/*
 * A question's evidence while it is being corrected (ported from the hl line): the quotes it carries now, each
 * with a Keep switch, the lines the reviewer has cited ("Yours", each removable), then "Cite a line". On develop
 * the saved quotes start switched ON — a reviewer drops what they disagree with — and a question whose evidence
 * is left as it opened keeps it as it is. At most MAX_QUOTES kept and cited together, and the same quote once
 * (the server's rule, evidenceKey).
 *
 *   draft: an evidenceDraft draft ({ saved, keep, cited, human })
 *   evidenceAt(row): the "Agent at 01:48" link for a quote, or null
 *   onKeep(index, on), onUncite(index), onCiteLine(); citeRef: the Cite a line button, for focus
 */

const noteOf = (draft) => {
  if (draft.human) {
    return draft.saved.length
      ? 'The evidence saved with the last correction. Switch off what no longer applies.'
      : 'No evidence was saved with the last correction.';
  }
  return draft.saved.length
    ? 'The AI’s quotes stay unless you switch them off.'
    : 'There are no quotes for this question yet.';
};

const EvidenceEditor = ({ draft, evidenceAt, onKeep, onUncite, onCiteLine, citeRef, hasTranscript = true, disabled = false }) => {
  const count = quoteCount(draft.keep, draft.cited);
  const full = count >= MAX_QUOTES;
  const at = (row) => (evidenceAt ? evidenceAt(row) || undefined : undefined);
  let citeTitle;
  if (full) citeTitle = '5 quotes is the most a question can carry';
  else if (!hasTranscript) citeTitle = 'This call has no transcript to cite.';

  return (
    <div className="rounded-xl border border-gray-200 bg-white px-3 py-3">
      <div className="flex items-baseline justify-between gap-2">
        <p className="text-xs font-semibold text-gray-700">Evidence</p>
        <span className="text-xs text-gray-500">{count} of {MAX_QUOTES}</span>
      </div>
      <p className="mt-0.5 text-xs text-gray-500">{noteOf(draft)}</p>

      {(draft.saved.length > 0 || draft.cited.length > 0) && (
        <ul className="mt-2.5 space-y-2">
          {draft.saved.map((row, i) => {
            const twice = !draft.keep[i] && alreadyOnQuestion(draft, row);
            return (
              <li key={`saved-${i}`} className="flex items-start gap-3">
                <label className="mt-0.5 inline-flex flex-shrink-0 items-center gap-1.5 text-xs font-medium text-gray-600">
                  <input
                    type="checkbox"
                    role="switch"
                    checked={Boolean(draft.keep[i])}
                    onChange={(e) => onKeep(i, e.target.checked)}
                    disabled={disabled || (!draft.keep[i] && full) || twice}
                    title={twice ? 'The same quote is already on this question.' : undefined}
                    aria-label={`Keep this quote: ${row.quote.slice(0, 60)}`}
                    className="h-4 w-4 rounded border-gray-300 text-cloudza-600"
                  />
                  Keep
                </label>
                <div className={`min-w-0 flex-1 ${draft.keep[i] ? '' : 'opacity-50'}`}>
                  <EvidenceQuote speaker={row.speaker} at={at(row)}>{row.quote}</EvidenceQuote>
                </div>
              </li>
            );
          })}
          {draft.cited.map((row, i) => (
            <li key={`cited-${i}`} className="flex items-start gap-3 rounded-lg border border-cloudza-200 bg-cloudza-50 px-2 py-1.5">
              <span className="mt-0.5 flex-shrink-0 rounded bg-white px-1.5 text-[11px] font-semibold text-cloudza-700">Yours</span>
              <div className="min-w-0 flex-1">
                <EvidenceQuote speaker={row.speaker} at={at(row)}>{row.quote}</EvidenceQuote>
              </div>
              <button
                type="button"
                onClick={() => onUncite(i)}
                disabled={disabled}
                aria-label="Remove this quote"
                title="Remove"
                className="flex-shrink-0 rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
              >
                <FaTimes className="h-3 w-3" aria-hidden="true" />
              </button>
            </li>
          ))}
        </ul>
      )}

      <button
        ref={citeRef}
        type="button"
        onClick={onCiteLine}
        disabled={disabled || full || !hasTranscript}
        title={citeTitle}
        className="btn-outline mt-2.5 !px-2.5 !py-1 !text-xs disabled:cursor-not-allowed disabled:opacity-50"
      >
        Cite a line
      </button>
    </div>
  );
};

export default EvidenceEditor;
